import { memo, useMemo } from 'react';
import sortBy from 'lodash.sortby';
import { format } from 'date-fns';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TableContainer from '@mui/material/TableContainer';
import { Typography } from '@components/Typography';
import { Table } from '@components/Table';
import { useTranslation, useLocations } from '@hooks';
import { analyticsService } from '@services';
import { formatNumber, formatPercentNumber } from '@helpers/formatNumbers';
import ExportCSV from '@features/Analytics/ExportCSV';

const Container = styled(Paper)({
    padding: '24px',
    width: '100%',
    borderRadius: 4,
    marginTop: '24px',
});

const TableCellText = styled(Typography)({
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
});

const Text = styled('span', { shouldForwardProp: prop => prop !== 'withIcon' })(({ withIcon }) => ({
    marginRight: '28px',
    ...(withIcon && {
        marginRight: '8px',
    }),
}));

export const GetTableHeader = () => {
    const { t } = useTranslation();
    return [
        {
            key: 0,
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('common.location')}
                </Typography>
            ),
        },
        {
            key: 1,
            component: (
                <Typography variant="subtitle2" align="right">
                    {t('AnalyticsReviews.totalReviews')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography variant="subtitle2" align="right">
                    {t('AnalyticsReviews.totalRepliesPercentage')}
                </Typography>
            ),
        },
        {
            key: 3,
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsReviews.averageRating')}
                </Typography>
            ),
        },
    ];
};

const getTableArrow = field => {
    if (field > 0) {
        return <ArrowUpwardIcon sx={{ color: '#0B8D00', fontSize: '20px' }} />;
    } else if (field < 0) {
        return <ArrowDownwardIcon sx={{ color: '#B00020', fontSize: '20px' }} />;
    }
    return null;
};

export const getTableBody = data => {
    return data.map(
        ({
            internalName,
            totalReviews,
            totalRepliesPercentage,
            averageRatingDelta,
            totalReviewsDelta,
            totalRepliesPercentageDelta,
            averageRating,
        }) => [
            {
                component: (
                    <Typography variant="subtitle2" align="left">
                        {internalName}
                    </Typography>
                ),
            },
            {
                component: (
                    <TableCellText variant="body2" align="right">
                        <Text withIcon={totalReviewsDelta !== 0}>{formatNumber(totalReviews)}</Text>
                        {getTableArrow(totalReviewsDelta)}
                    </TableCellText>
                ),
            },
            {
                component: (
                    <TableCellText variant="body2" align="right">
                        <Text withIcon={totalRepliesPercentageDelta !== 0}>
                            {formatPercentNumber(totalRepliesPercentage)}
                        </Text>
                        {getTableArrow(totalRepliesPercentageDelta)}
                    </TableCellText>
                ),
            },
            {
                component: (
                    <TableCellText variant="body2" align="right">
                        <Text withIcon={averageRatingDelta !== 0}>{formatNumber(averageRating)}</Text>
                        {getTableArrow(averageRatingDelta)}
                    </TableCellText>
                ),
            },
        ],
    );
};

const ReviewsTable = memo(({ isLoading, data, filter }) => {
    const { locationsMap } = useLocations();
    const { t } = useTranslation();

    const dataSortedByName = useMemo(() => {
        const dataWithLocationNames = data.map(item => ({
            ...item,
            internalName: locationsMap[item.locationId]?.internalName || '',
        }));
        return sortBy(dataWithLocationNames, item => item.internalName.toLowerCase());
    }, [data]);

    const onExport = () =>
        analyticsService.exportAnalyticsCSV(
            {
                ...filter,
                profileTypes: filter.integrations,
                locations: filter.locations.map(item => item.id),
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
            },
            'reviews',
        );

    return (
        <Container elevation={1}>
            <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}
            >
                <Typography variant="title1" sx={{ alignSelf: 'center' }}>
                    {t(`Header.analyticsReviews`)}
                </Typography>
                <ExportCSV type="reviews" apiCall={onExport} />
            </div>
            <TableContainer>
                <Table columnNames={GetTableHeader()} rows={getTableBody(dataSortedByName)} isDataLoading={isLoading} />
            </TableContainer>
        </Container>
    );
});

ReviewsTable.displayName = 'ReviewsTable';

export default ReviewsTable;
